import { CollabProvider } from "@adea/product/collab/client";
import { AvatarPhotos, Toaster, TooltipProvider } from "@adea/ui";
import { createFileRoute, Outlet, redirect, retainSearchParams, useRouter } from "@tanstack/react-router";
import { type ReactNode, useEffect, useMemo } from "react";
import { CompanyList } from "../components/CompanyList.tsx";
import { NoAccess } from "../components/NoAccess.tsx";
import { Shell } from "../components/Shell.tsx";
import { SignInMethod } from "../components/SignInMethod.tsx";
import { ErrorState, FullPageLoading } from "../components/states.tsx";
import { ConnectHost } from "../connect/ConnectHost.tsx";
import { CONNECT_KEYS, validateConnectSearch } from "../connect/search.ts";
import { actions } from "../lib/actions.ts";
import { ApiRequestError } from "../lib/api.ts";
import { ConfirmHost } from "../lib/confirm.tsx";
import { useFormat, useT } from "../lib/i18n.tsx";
import { LiveProvider, useLiveChannel } from "../lib/live.ts";
import { type CompanyLink, type Me, meQuery, useMe } from "../lib/session.ts";
import { setTheme } from "../lib/theme.ts";
import { ReachHost } from "../reach/index.ts";
import { DemoHost } from "../screens/demo/DemoHost.tsx";

/**
 * Everything behind sign-in. The company comes from the address: the API reads it from the host and
 * checks the membership, so two tabs on two addresses show two companies.
 *
 * The layout holds what every screen shares: the shell, the live channel (refetches what changed, carries the runs a
 * screen follows), the connect panel (`?connect=`, kept from page to page), the confirmation dialog the gate can ask
 * for, and the toasts.
 */
export const Route = createFileRoute("/_app")({
  validateSearch: (search: Record<string, unknown>) => validateConnectSearch(search),
  /* the connect panel stays open from page to page until closed */
  search: { middlewares: [retainSearchParams([...CONNECT_KEYS])] },
  beforeLoad: async ({ context, location }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 401) {
        throw redirect({ to: "/sign-in", search: { next: location.href } });
      }
      throw error;
    }
  },
  // What every page's frame reads (the photos, the bell), asked for alongside the page's own data instead of after
  // the frame has drawn. Not waited for.
  loader: ({ context }) => {
    for (const read of [
      actions.queryOptions("team.list", {}),
      actions.queryOptions("notifications.list", { limit: 30 }),
    ])
      void context.queryClient.prefetchQuery(read as never);
  },
  pendingComponent: FullPageLoading,
  errorComponent: AppError,
  component: AppLayout,
});

function AppLayout() {
  const me = useMe();
  // The server's copy of the choice wins, so a change on another device shows here at the next load.
  const theme = me.person.theme;
  useEffect(() => {
    if (theme) setTheme(theme);
  }, [theme]);
  if (!me.company) return <PickCompany me={me} />;
  return (
    <LiveProvider companySlug={me.company.slug}>
      <CollabMount me={me}>
        <PhotoMount>
          <TooltipProvider>
            <Shell me={me}>
              <Outlet />
            </Shell>
            <ConnectHost />
            <ConfirmHost />
            <ReachHost />
            <DemoHost />
            <Toaster />
          </TooltipProvider>
        </PhotoMount>
      </CollabMount>
    </LiveProvider>
  );
}

/** Comments, follows, presence and text written together: one provider, on the tab's live channel. */
function CollabMount({ me, children }: { me: Me; children: ReactNode }) {
  const live = useLiveChannel();
  const t = useT();
  const format = useFormat();
  const transport = useMemo(() => live?.collabTransport() ?? null, [live]);
  const who = useMemo(() => ({ id: me.person.id, name: me.person.name }), [me.person.id, me.person.name]);
  return (
    <CollabProvider
      actions={actions}
      transport={transport}
      me={who}
      isAdmin={me.company?.role === "admin"}
      t={t}
      format={format}
    >
      {children}
    </CollabProvider>
  );
}

/** Everyone's photo, once for the whole app: an avatar anywhere shows the photo of the person it names. */
function PhotoMount({ children }: { children: ReactNode }) {
  const team = actions.useActionQuery("team.list", {});
  const { byId, byName } = useMemo(() => {
    const ids: Record<string, string> = {};
    const names: Record<string, string> = {};
    const seen = new Set<string>();
    const twice = new Set<string>();
    for (const m of team.data?.members ?? []) {
      if (m.photoUrl) ids[m.id] = m.photoUrl;
      const k = m.name.trim().toLowerCase();
      if (seen.has(k)) twice.add(k);
      seen.add(k);
    }
    // A name stands in for an id only where it is unique, so two people with one name never share a photo.
    for (const m of team.data?.members ?? []) {
      if (m.photoUrl && !twice.has(m.name.trim().toLowerCase())) names[m.name] = m.photoUrl;
    }
    return { byId: ids, byName: names };
  }, [team.data]);
  return (
    <AvatarPhotos byId={byId} photos={byName}>
      {children}
    </AvatarPhotos>
  );
}

function AppError({ error }: { error: unknown }) {
  const router = useRouter();
  if (error instanceof ApiRequestError && error.code === "no_access") {
    const details = (error.details ?? {}) as { email?: string; companies?: CompanyLink[] };
    return <NoAccess email={details.email ?? ""} companies={details.companies ?? []} />;
  }
  if (error instanceof ApiRequestError && error.code === "sign_in_method") {
    const details = (error.details ?? {}) as { email?: string; providers?: ("google" | "microsoft")[] };
    return <SignInMethod providers={details.providers ?? []} email={details.email ?? ""} />;
  }
  return <ErrorState error={error} onRetry={() => void router.invalidate()} />;
}

/** Opened on an address that isn't a company's: offer the person's companies. */
function PickCompany({ me }: { me: Me }) {
  const t = useT();
  return (
    <main className="mx-auto flex min-h-dvh max-w-lg flex-col justify-center gap-6 px-4 py-12">
      <div>
        <h1 className="text-[26px] font-semibold tracking-tight">{t("auth.pickCompany.title")}</h1>
        <p className="mt-2 text-muted">{t("auth.pickCompany.lead", { email: me.person.email })}</p>
      </div>
      {me.companies.length > 0 ? (
        <CompanyList companies={me.companies} />
      ) : (
        <p className="text-muted">{t("auth.pickCompany.none")}</p>
      )}
    </main>
  );
}
